import type { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table'
import { useMemo } from 'react'

import type { FeedbackSortBy, FeedbackType } from '@/services/api/api.types'

interface UseFeedbackFiltersParams {
  columnFilters: MRT_ColumnFiltersState
  sorting: MRT_SortingState
}

interface FeedbackFilters {
  topicIds?: string[]
  contentModuleIds?: string[]
  feedbackType?: FeedbackType
  tags?: string[]
  comments?: string
  startDate?: string
  endDate?: string
  sortBy?: FeedbackSortBy
  sortOrder?: 'asc' | 'desc'
}

export function useFeedbackFilters({ columnFilters, sorting }: UseFeedbackFiltersParams): FeedbackFilters {
  return useMemo(() => {
    const filters: FeedbackFilters = {}

    // Extract topic filter
    const topicFilter = columnFilters.find(f => f.id === 'cmTopics')
    const selectedTopicIds = topicFilter?.value as string[] | undefined
    if (selectedTopicIds && selectedTopicIds.length > 0) {
      filters.topicIds = selectedTopicIds
    }

    // Extract content module filter
    const contentModuleFilter = columnFilters.find(f => f.id === 'contentModuleId')
    const selectedContentModuleIds = contentModuleFilter?.value as string[] | undefined
    if (selectedContentModuleIds && selectedContentModuleIds.length > 0) {
      filters.contentModuleIds = selectedContentModuleIds
    }

    // Extract feedback type filter
    const feedbackTypeFilter = columnFilters.find(f => f.id === 'annotationKind')
    const selectedFeedbackType = feedbackTypeFilter?.value as string | undefined
    if (selectedFeedbackType) {
      filters.feedbackType = selectedFeedbackType as FeedbackType
    }

    // Extract tag filter
    const tagFilter = columnFilters.find(f => f.id === 'tag')
    const selectedTags = tagFilter?.value as string[] | undefined
    if (selectedTags && selectedTags.length > 0) {
      filters.tags = selectedTags
    }

    // Extract date filter
    const dateFilter = columnFilters.find(f => f.id === 'createdAt')
    const dateRange = dateFilter?.value as [Date | null, Date | null] | undefined
    if (dateRange?.[0]) {
      filters.startDate = dateRange[0].toISOString()
    }
    if (dateRange?.[1]) {
      filters.endDate = dateRange[1].toISOString()
    }

    // Extract comments filter
    const commentsFilter = columnFilters.find(f => f.id === 'userFeedback')
    const commentsSearchTerm = commentsFilter?.value as string | undefined
    if (commentsSearchTerm) {
      filters.comments = commentsSearchTerm
    }

    // Convert sorting to API format
    if (sorting.length > 0) {
      const sortBy = sorting[0].id
      const sortOrder = sorting[0].desc ? 'desc' : 'asc'

      // Map column IDs to API field names
      const sortByMapping: Record<string, FeedbackSortBy> = {
        createdAt: 'date',
        cmTopics: 'topic',
        contentModuleId: 'content_module',
        annotationKind: 'feedback_type',
        tag: 'tag',
      }

      if (sortByMapping[sortBy]) {
        filters.sortBy = sortByMapping[sortBy]
        filters.sortOrder = sortOrder
      }
    }

    return filters
  }, [columnFilters, sorting])
}
